// src/components/input/Input.js
import React, { useId, useState } from 'react';
import { FaSearch, FaPlus } from 'react-icons/fa';
import './Input.css';
import { useModalForm } from '../modalForm/ModalFormContext';
import { formatNumeric, normalizeDecimalInputBr } from '../../utils/numberUtils';

const applyMask = (value, mask) => {
  const numbersOnly = (value || '').toString().replace(/\D/g, '');

  if (!mask) {
    return value;
  }

  let maskedValue = '';
  let numberIndex = 0;

  for (let i = 0; i < mask.length; i += 1) {
    const maskChar = mask[i];

    if (maskChar === '9') {
      if (numberIndex >= numbersOnly.length) {
        break;
      }

      maskedValue += numbersOnly[numberIndex];
      numberIndex += 1;
    } else {
      if (numberIndex >= numbersOnly.length) {
        break;
      }

      maskedValue += maskChar;
    }
  }

  return maskedValue;
};

const Input = ({
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  disabled,
  readOnly,
  tabIndex,
  msgErro,
  style,
  type,
  size,
  autoFocus,
  btnBuscar,
  btnAdd,
  btnBuscarDisabled = false,
  btnAddDisabled = false,
  btnTabIndex,
  mask,
  // Modo monetário com precisão configurável, por exemplo: decimalPlaces={3}.
  monetary = false,
  decimalPlaces = 2,
  rows,
  maxLength,
  hidden,
  inputRef,  
  onKeyDown,
  onFocus,
  inputMode,
  labelHidden = false,
  id,
  title,
}) => {
  const generatedId = useId();
  const [monetaryFocused, setMonetaryFocused] = useState(false);
  const inputId = id || generatedId;
  const { variant } = useModalForm();
  const isMonetary = monetary && rows === undefined && !mask;
  const monetaryValue = !isMonetary || value === '' || value === null || value === undefined
    ? value ?? ''
    : monetaryFocused && typeof value === 'string'
      ? value
      : formatNumeric(value, decimalPlaces);
  // Define a largura com base na propriedade size.
  const getWidth = () => {
    if (size === undefined) return '100px';
    return typeof size === 'number' ? `${size}px` : size;
  };

  const getInputStyle = () => {
    const width = getWidth();

    return {
      ...(isMonetary ? { textAlign: 'right' } : {}),
      ...style,
      width,
      maxWidth: '100%',
      flex: `0 1 ${width}`,
    };
  };

  // 👇 Se estiver invisível, não renderiza nada
  if (hidden) {
    return null;
  }

  const handleKeyDown = (e) => {
    if (e.key === 'F2' && typeof btnBuscar === 'function' && !btnBuscarDisabled) {
      e.preventDefault();
      btnBuscar(e);
      return;
    }
    onKeyDown?.(e);
  };

  const handleMaskedChange = (e) => {
    if (!onChange) {
      return;
    }

    const maskedValue = applyMask(e.target.value, mask);

    onChange({
      ...e,
      target: {
        ...e.target,
        value: maskedValue,
      },
    });
  };

  const handleMonetaryChange = (e) => {
    e.target.value = normalizeDecimalInputBr(e.target.value, decimalPlaces);
    onChange?.(e);
  };

  const handleMonetaryFocus = (e) => {
    setMonetaryFocused(true);
    onFocus?.(e);
  };

  const handleMonetaryBlur = (e) => {
    setMonetaryFocused(false);
    if (!disabled && !readOnly) {
      e.target.value = formatNumeric(e.target.value, decimalPlaces);
      onChange?.(e);
    }
    onBlur?.(e);
  };

  return (
    <div className="form-group">
      <label className={`infomaio-field-label${labelHidden ? ' visually-hidden' : ''}`} htmlFor={inputId}>{label || '\u00A0'}</label>
      <div className="input-wrapper">
        {rows !== undefined ? (
          <textarea
            id={inputId}
            title={title}
            aria-describedby={msgErro ? `${inputId}-error` : undefined}
            ref={inputRef}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            className={`form-control form-input ${msgErro ? 'is-invalid' : ''}`}
            aria-invalid={msgErro ? 'true' : 'false'}
            disabled={disabled}
            readOnly={readOnly}
            tabIndex={tabIndex}
            style={getInputStyle()}
            placeholder={placeholder}
            autoFocus={autoFocus}
            data-modal-autofocus={autoFocus || undefined}
            rows={rows}
            maxLength={maxLength}
          />
        ) : mask ? (
          <input
            id={inputId}
            title={title}
            aria-describedby={msgErro ? `${inputId}-error` : undefined}
            ref={inputRef}
            value={applyMask(value, mask)}
            onChange={handleMaskedChange}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            inputMode={inputMode}
            type={type || 'text'}
            className={`form-control form-input ${msgErro ? 'is-invalid' : ''}`}
            aria-invalid={msgErro ? 'true' : 'false'}
            disabled={disabled}
            readOnly={readOnly}
            tabIndex={tabIndex}
            style={getInputStyle()}
            placeholder={placeholder}
            autoFocus={autoFocus}
            data-modal-autofocus={autoFocus || undefined}
            maxLength={maxLength}
          />
        ) : (
          <input
            id={inputId}
            title={title}
            aria-describedby={msgErro ? `${inputId}-error` : undefined}
            ref={inputRef}
            value={isMonetary ? monetaryValue : value}
            onChange={isMonetary ? handleMonetaryChange : onChange}
            onBlur={isMonetary ? handleMonetaryBlur : onBlur}
            onKeyDown={handleKeyDown}
            onFocus={isMonetary ? handleMonetaryFocus : onFocus}
            inputMode={isMonetary ? 'decimal' : inputMode}
            type={isMonetary ? 'text' : type || 'text'}
            className={`form-control form-input ${msgErro ? 'is-invalid' : ''}`}
            aria-invalid={msgErro ? 'true' : 'false'}
            disabled={disabled}
            readOnly={readOnly}
            tabIndex={tabIndex}
            style={getInputStyle()}
            placeholder={placeholder}
            autoFocus={autoFocus}
            data-modal-autofocus={autoFocus || undefined}
            maxLength={maxLength}
          />
        )}
        {btnBuscar && (
          <button
            className="btn btn-input"
            style={{ padding: '6px' }}
            type="button"
            onClick={btnBuscar}
            disabled={btnBuscarDisabled}
            tabIndex={btnTabIndex ?? (variant === 'legacy' ? -1 : 0)}
            aria-label={`Buscar ${label || 'registro'} (F2)`}
          >
            <FaSearch aria-hidden="true" />
          </button>
        )}
        {btnAdd && (
          <button
            className="btn btn-input"
            type="button"
            onClick={btnAdd}
            disabled={btnAddDisabled}
            style={{
              padding: '6px',
            }}
            tabIndex={btnTabIndex ?? (variant === 'legacy' ? -1 : 0)}
            aria-label={`Adicionar ${label || 'registro'}`}
          >
            <FaPlus aria-hidden="true" />
          </button>
        )}
      </div>
      {msgErro && <span className="error-input" id={`${inputId}-error`}>{msgErro}</span>}
    </div>
  );
};

export default Input;
